import { useState } from "react";
import { Lock, ArrowRight, Shield } from "lucide-react";
import { resolveAccessCode } from "@/data/accessCodes";
import { useLang } from "@/i18n";

export const LoginGate = ({ onAuth }) => {
  const { t } = useLang();
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const entry = resolveAccessCode(code);
    if (entry) {
      onAuth(code);
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center px-4 py-10" data-testid="login-gate">
      <div className={`w-full max-w-sm ${shake ? "animate-shake" : ""}`}>
        <div className="bg-white border border-[#e8e8e8] rounded-sm p-8">
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src="/coca-cola-hbc-logo.png" alt="Coca-Cola HBC" className="h-14 w-auto object-contain" />
            <div className="w-[1px] h-8 bg-[#e8e8e8]" />
            <img src="/fora-logo.png" alt="FORA" className="h-7 w-auto object-contain" />
          </div>

          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-12 rounded-sm bg-[#000000] flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
          </div>
          <h1 className="font-condensed text-2xl font-black uppercase tracking-tight text-[#000000] text-center mb-1">
            {t("Access Required")}
          </h1>
          <p className="text-[11px] text-[#555555] text-center mb-6 uppercase tracking-[0.15em] font-semibold">
            {t("Enter the access code to view FORA Onboard")}
          </p>
          <form onSubmit={handleSubmit}>
            <input
              type="password"
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(false); }}
              placeholder={t("Enter access code")}
              className={`w-full px-4 py-3 bg-[#fafafa] border ${error ? "border-[#dd260e]" : "border-[#e8e8e8]"} rounded-sm text-sm text-[#000000] placeholder:text-[#555555]/50 focus:outline-none focus:border-[#000000] transition-colors mb-3`}
              autoFocus
              data-testid="login-code-input"
            />
            {error && (
              <p className="text-xs text-[#dd260e] mb-3" data-testid="login-error">
                {t("Invalid access code. Please try again.")}
              </p>
            )}
            <button type="submit" className="w-full bg-[#000000] hover:bg-[#dd260e] text-white py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] rounded-sm transition-colors flex items-center justify-center gap-3" data-testid="login-submit-btn">
              {t("Enter FORA Onboard")} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>

          {/* Confidentiality notice — Login Screen */}
          <div className="mt-6 pt-6 border-t border-[#e8e8e8]" data-testid="login-disclaimer">
            <div className="flex items-start gap-2.5">
              <Shield className="w-3.5 h-3.5 text-[#dd260e] mt-0.5 shrink-0" strokeWidth={2} />
              <div className="text-[10px] text-[#555555] leading-relaxed space-y-1.5">
                <p>
                  {t("This portal contains")} <strong className="text-[#000000]">{t("confidential internal business information")}</strong> {t("intended solely for authorized users within")} <strong className="text-[#000000]">Coca-Cola HBC</strong> {t("and")} <strong className="text-[#000000]">Master F&amp;B</strong>.
                </p>
                <p>
                  {t("The content is provided for")} <strong className="text-[#000000]">{t("informational and enablement purposes only")}</strong> {t("and does not constitute a commercial offer or contractual commitment.")}
                </p>
                <p>
                  {t("Unauthorized access, sharing, copying, or external distribution of any content is strictly prohibited.")}
                </p>
              </div>
            </div>
          </div>
        </div>
        <p className="text-[10px] text-[#555555] text-center mt-6 mb-2">
          {t("Confidential — Internal use only")}
        </p>
        <div className="text-center">
          <p className="text-[10px] text-[#555555] mb-1.5">{t("Need access? Contact:")}</p>
          <div className="flex flex-col gap-0.5">
            <a href="mailto:martina.lovetinska@cchellenic.com" className="text-[10px] text-[#dd260e] hover:underline">Martina Lovetinska — martina.lovetinska@cchellenic.com</a>
            <a href="mailto:pero.pehar@master-fb.com" className="text-[10px] text-[#dd260e] hover:underline">Pero Pehar — pero.pehar@master-fb.com</a>
            <a href="mailto:igor.guberovic@cchellenic.com" className="text-[10px] text-[#dd260e] hover:underline">Igor Guberovic — igor.guberovic@cchellenic.com</a>
            <a href="mailto:katarzyna.stanczak@cchellenic.com" className="text-[10px] text-[#dd260e] hover:underline">Katarzyna Stanczak — katarzyna.stanczak@cchellenic.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};
